// JobForm — create or edit a job.
//
// The classic app has this on two screens (Jobs and Schedule); the rebuild
// shipped with no way to create a job at all, which is the first row of
// docs/REBUILD_PARITY.md. One sheet serves every caller — Office Jobs, the
// Dispatch board, the schedule — so there is exactly one place a job is
// described and one place to fix when the shape changes.
//
//   POST /api/jobs        create
//   PUT  /api/jobs/:id    edit
//
// The rules (what is sent, what a role may set, how the property list follows
// the client) live in jobFields.js with no React around them, so they are
// tested directly rather than through the DOM.
//
// Deliberately self-contained: it does not import Office's page helpers,
// because Dispatch and the schedule open it too and an app should not have to
// pull in another app to create a job.
//
// ── Why `listErrors` exists ─────────────────────────────────────────────────
//
// The client, property and crew pickers are filled from three requests this
// form does not make. Every caller used to hand them over as `q.data || []`,
// which collapses "the list is empty" and "the request was refused" into the
// same empty array — and an empty <select> reads as DATA, not as a failure.
// docs/AGENT_LEARNINGS.md (2026-08-17) records the same shape on the classic
// Estimates screen: a roster that 401'd rendered a dropdown offering only
// "— New client —", the reader concluded the customer was not on file, and the
// estimate saved with no client_id.
//
// Here it is worse than a duplicate customer. A job saved with no client and
// no property has no address for the crew to drive to, and a job saved with
// nobody on it appears on nobody's phone — and the person who did it was told
// nothing. So each list carries the ERROR that stopped it arriving, and the
// field says so.
//
// The sentence comes from `pickerNote`, which five Office pickers already use
// — Activity, Media, Reports and the property form. It was in Office's page
// helpers, which this form deliberately does not import, which is exactly why
// this form was the one that had never been fixed; it now lives in
// apps/shared/pickerNote.js. The controls stay usable either way: creating a
// job without a client is a real fallback, it just must not happen silently.

import React, { useEffect, useMemo, useState } from 'react';
import { Sheet, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, CheckRow } from '../../ui/fw';
import { post, put, describeError } from './http';
import { pickerNote } from './pickerNote';
import {
  JOB_STATUSES, JOB_TYPES, RECURRENCES, PAY_TYPES,
  STATUS_LABELS, JOB_TYPE_LABELS, RECURRENCE_LABELS, PAY_TYPE_LABELS,
  seedJob, jobBody, validateJob, canAssignOthers, applyClientChange,
  applyLeadChange, toggleAssignee, propertyOptions,
} from './jobFields';

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {object}   [props.job]         existing row to edit; omit to create
 * @param {string}   props.role          the signed-in person's role
 * @param {Array}    [props.clients]     [{ id, name }]
 * @param {Array}    [props.properties]  [{ id, client_id, name }]
 * @param {Array}    [props.workers]     [{ id, name }] — assignable people
 * @param {object}   [props.listErrors]  { clients, properties, workers } — the
 *                                       ERROR that stopped each list arriving,
 *                                       so an empty picker is not shown as an
 *                                       empty list. The error itself, not a
 *                                       boolean: pickerNote says something
 *                                       different for a 403 than for a 500
 * @param {string}   [props.defaultDate] YYYY-MM-DD to preselect (from a calendar day)
 * @param {Function} props.onClose
 * @param {Function} [props.onSaved]     called with the saved job
 */
export default function JobForm({
  open, job, role = 'worker', clients = [], properties = [], workers = [],
  listErrors = {}, defaultDate = '', onClose, onSaved,
}) {
  const editing = !!(job && job.id);
  const [form, setForm] = useState(() => seedJob(job, { defaultDate }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setForm(seedJob(job, { defaultDate })); setError(null); }
  }, [open, job, defaultDate]);

  const mayAssign = canAssignOthers(role);
  // What the picker OFFERS: the client's properties plus whatever this job is
  // already carrying (#549). A select whose value matches no option renders
  // blank, so the person read "No property" over a property_id the save still
  // sent. See propertyOptions for why sending it is right and showing it was
  // the missing half.
  const propOptions = useMemo(
    () => propertyOptions(properties, form, { propertyName: job && job.property_name }),
    [properties, form, job],
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onClient = (e) => setForm((f) => applyClientChange(f, e.target.value, properties));
  const toggleCrew = (id) => setForm((f) => toggleAssignee(f, id));

  const submit = async (e) => {
    e.preventDefault();
    const bad = validateJob(form);
    if (bad) { setError(bad); return; }
    setBusy(true);
    setError(null);
    try {
      const body = jobBody(form, { role, editing });
      const saved = editing ? await put(`/jobs/${job.id}`, body) : await post('/jobs', body);
      if (onSaved) await onSaved(saved);
      if (onClose) onClose();
    } catch (err) {
      setError(describeError(err, editing ? 'Could not save the job.' : 'Could not create the job.'));
    } finally {
      setBusy(false);
    }
  };

  const formId = 'fw-job-form';
  const footer = (
    <div className="fw-jobform__footer">
      <FwButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</FwButton>
      <FwButton variant="primary" type="submit" form={formId} loading={busy}>
        {editing ? 'Save job' : 'Create job'}
      </FwButton>
    </div>
  );

  return (
    <Sheet open={open} title={editing ? 'Edit job' : 'New job'} onClose={onClose} footer={footer} className="fw-jobform">
      <form id={formId} onSubmit={submit} className="fw-form">
        {error ? <Notice tone="stop" title="Not saved">{error}</Notice> : null}

        <FwField label="Title" htmlFor="job-title" required>
          <FwInput id="job-title" value={form.title} onChange={set('title')} autoFocus maxLength={200} />
        </FwField>

        <FwField label="Description" htmlFor="job-desc" hint="What the crew needs to know before they arrive.">
          <FwTextarea id="job-desc" rows={3} value={form.description} onChange={set('description')} />
        </FwField>

        <FwField
          label="Client"
          htmlFor="job-client"
          error={pickerNote(listErrors.clients, 'client')}
        >
          <FwSelect id="job-client" value={form.client_id} onChange={onClient}>
            <option value="">No client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </FwSelect>
        </FwField>

        <FwField
          label="Property"
          htmlFor="job-property"
          error={pickerNote(listErrors.properties, 'property')}
          hint={form.client_id ? undefined : 'Pick a client first.'}
        >
          <FwSelect
            id="job-property"
            value={form.property_id}
            onChange={set('property_id')}
            disabled={!form.client_id && !propOptions.length}
          >
            <option value="">No property</option>
            {propOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.onList ? p.name : `${p.name} (not on this client)`}</option>
            ))}
          </FwSelect>
        </FwField>

        {mayAssign ? (
          <>
            {/* The crew list is the one whose absence has a consequence worth
                spelling out — a job saved with nobody on it is on nobody's
                phone — so pickerNote's sentence carries that clause after it
                rather than being replaced by it. */}
            <FwField
              label="Lead worker"
              htmlFor="job-lead"
              error={listErrors.workers
                ? `${pickerNote(listErrors.workers, 'worker')} Saving now leaves this job unassigned, and it will not appear on anyone's phone.`
                : null}
              hint="Payroll and the worker's own job list follow the lead."
            >
              <FwSelect id="job-lead" value={form.user_id} onChange={(e) => setForm((f) => applyLeadChange(f, e.target.value))}>
                <option value="">Unassigned</option>
                {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </FwSelect>
            </FwField>

            {workers.length ? (
              <fieldset className="fw-jobform__crew">
                <legend>Crew</legend>
                {workers.map((w) => (
                  <CheckRow
                    key={w.id}
                    checked={form.assignee_ids.includes(String(w.id))}
                    onToggle={() => toggleCrew(w.id)}
                    disabled={String(w.id) === String(form.user_id)}
                    meta={String(w.id) === String(form.user_id) ? 'Leads this job' : undefined}
                    label={w.name}
                  />
                ))}
              </fieldset>
            ) : null}
          </>
        ) : null}

        <div className="fw-jobform__pair">
          <FwField label="Status" htmlFor="job-status">
            <FwSelect id="job-status" value={form.status} onChange={set('status')}>
              {JOB_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </FwSelect>
          </FwField>
          <FwField label="Type" htmlFor="job-type">
            <FwSelect id="job-type" value={form.job_type} onChange={set('job_type')}>
              <option value="">Not set</option>
              {JOB_TYPES.map((t) => <option key={t} value={t}>{JOB_TYPE_LABELS[t]}</option>)}
            </FwSelect>
          </FwField>
        </div>

        <div className="fw-jobform__pair">
          <FwField label="Scheduled" htmlFor="job-start">
            <FwInput id="job-start" type="date" value={form.scheduled_date} onChange={set('scheduled_date')} />
          </FwField>
          <FwField label="Until" htmlFor="job-end" hint="Leave blank for a single day.">
            <FwInput id="job-end" type="date" value={form.scheduled_end_date} onChange={set('scheduled_end_date')} />
          </FwField>
        </div>

        <FwField label="Repeats" htmlFor="job-recur">
          <FwSelect id="job-recur" value={form.recurrence} onChange={set('recurrence')}>
            <option value="">Does not repeat</option>
            {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABELS[r]}</option>)}
          </FwSelect>
        </FwField>

        <FwField label="Estimated minutes" htmlFor="job-est" hint="Whole minutes. Leave blank if you do not know.">
          <FwInput id="job-est" inputMode="numeric" value={form.estimated_time} onChange={set('estimated_time')} />
        </FwField>

        <div className="fw-jobform__pair">
          <FwField label="Pay amount" htmlFor="job-pay">
            <FwInput id="job-pay" inputMode="decimal" value={form.pay_amount} onChange={set('pay_amount')} />
          </FwField>
          <FwField label="Pay type" htmlFor="job-paytype">
            <FwSelect id="job-paytype" value={form.pay_type} onChange={set('pay_type')}>
              {PAY_TYPES.map((p) => <option key={p} value={p}>{PAY_TYPE_LABELS[p]}</option>)}
            </FwSelect>
          </FwField>
        </div>

        {editing ? (
          <FwField label="Note about this change" htmlFor="job-note" hint="Added to the job's activity trail.">
            <FwInput id="job-note" value={form.status_note} onChange={set('status_note')} maxLength={200} />
          </FwField>
        ) : null}
      </form>
    </Sheet>
  );
}
